import { useState, useRef, useEffect, useCallback } from 'react';
import { useProjectStore, useUIStore, useAuthStore } from '@/stores';
import { Modal, Button, Stepper, AnimatedCheckbox, Loader } from '@/components/ui';
import { 
  FileText, 
  Upload, 
  Edit3, 
  Sparkles,
  FileSpreadsheet,
  AlertCircle,
  Check,
  Eye
} from 'lucide-react';
import type { VehicleNodes, Project, Task } from '@/types';
import { sgmDefaultTemplate, calculateTaskDates, extractFunctionalNodeMapping, replaceFunctionalNodesInTemplate, extractCustomVehicleNodes } from '@/data/templates';
import { ComponentTemplateSelectModal } from './ComponentTemplateSelectModal';
import type { ComponentTemplate } from './ComponentTemplateSelectModal';

interface MilestoneItem {
  id: string;
  sequence: number;
  name: string;
  description: string;
  required: boolean;
  functionalNode: '项目立项' | '数据发布' | '首次造车' | '可销售车' | 'N/A';
}

interface VehicleTemplate {
  id: string;
  oem: string;
  description: string;
  milestones: MilestoneItem[];
  created_at: string;
  updated_at: string;
  isDefault?: boolean;
}

const defaultSGMTemplate: VehicleTemplate = {
  id: 'default-sgm',
  oem: 'SGM',
  description: 'SGM 默认整车里程碑模板',
  milestones: [
    { id: '1', sequence: 1, name: 'VPI', description: 'Vehicle Program Initiation', required: true, functionalNode: '项目立项' },
    { id: '2', sequence: 2, name: 'SF', description: 'Styling Freeze', required: false, functionalNode: 'N/A' },
    { id: '3', sequence: 3, name: 'VDR', description: 'Verified Data Release', required: true, functionalNode: '数据发布' },
    { id: '4', sequence: 4, name: 'MC1', description: 'Matching 1', required: false, functionalNode: 'N/A' },
    { id: '5', sequence: 5, name: 'APPV', description: 'Advanced Process Validation Vehicle', required: true, functionalNode: '首次造车' },
    { id: '6', sequence: 6, name: 'MC2', description: 'Matching 2', required: false, functionalNode: 'N/A' },
    { id: '7', sequence: 7, name: 'PPV', description: 'Process Validation Vehicle', required: false, functionalNode: 'N/A' },
    { id: '8', sequence: 8, name: 'VTC', description: 'Vehicle Testing Complete', required: false, functionalNode: 'N/A' },
    { id: '9', sequence: 9, name: 'VDC', description: 'Validation Documentation Complete', required: false, functionalNode: 'N/A' },
    { id: '10', sequence: 10, name: 'S', description: 'Salable', required: true, functionalNode: '可销售车' },
    { id: '11', sequence: 11, name: 'SORP', description: 'Start Of Regular Production', required: true, functionalNode: 'N/A' },
  ],
  created_at: '',
  updated_at: '',
  isDefault: true
};

const steps = [
  { id: 1, title: 'Template', description: 'Select OEM Template' },
  { id: 2, title: 'Import', description: 'Creation method' },
  { id: 3, title: 'Project Info', description: 'Fill project details' },
  { id: 4, title: 'Complete', description: 'Project created' },
];

async function extractProjectInfoFromDocument(file: File): Promise<{
  projectName?: string;
  nodes?: Partial<VehicleNodes>;
  success: boolean;
  error?: string;
}> {
  try {
    console.log('=== 开始解析文档 ===');
    console.log('文件名:', file.name);
    console.log('文件类型:', file.type);
    console.log('文件大小:', file.size);
    
    const arrayBuffer = await file.arrayBuffer();
    const base64Content = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ''
      )
    );
    
    console.log('调用服务器 API 解析文档...');
    
    const response = await fetch('/api/parse-document', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileContent: base64Content,
        fileName: file.name,
      }),
    });

    console.log('服务器响应状态:', response.status);
    
    const data = await response.json();
    console.log('服务器响应数据:', data);
    
    if (!response.ok || !data.success) {
      return {
        success: false,
        error: data.error || `服务器错误: ${response.status}`,
      };
    }

    return {
      projectName: data.projectName,
      nodes: data.nodes,
      success: true,
    };
  } catch (error) {
    console.error('文档解析错误:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export function NewProjectWizard() {
  const { isProjectModalOpen, closeProjectModal, showToast, openTemplateShopModal } = useUIStore();
  const { createProject, generateTiming, setCurrentProject } = useProjectStore();
  const { user } = useAuthStore();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedTemplateData, setSelectedTemplateData] = useState<VehicleTemplate | null>(null);
  const [vehicleTemplates, setVehicleTemplates] = useState<VehicleTemplate[]>([defaultSGMTemplate]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creationMethod, setCreationMethod] = useState<'import' | 'manual' | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiExtractedData, setAiExtractedData] = useState<{
    projectName?: string;
    nodes?: Partial<VehicleNodes>;
  } | null>(null);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [supplier, setSupplier] = useState('');
  const [nodes, setNodes] = useState<Partial<VehicleNodes>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const [showComponentTemplateModal, setShowComponentTemplateModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<VehicleTemplate | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getAccessToken = (): string | null => {
    const storedSession = localStorage.getItem('session');
    if (!storedSession) return null;
    try {
      const session = JSON.parse(storedSession);
      return session?.access_token || null;
    } catch {
      return null;
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      const validExtensions = ['.xlsx', '.xls', '.csv', '.pdf', '.doc', '.docx', '.txt'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
        setUploadedFile(file);
        setAiExtractedData(null);
      } else {
        showToast('Unsupported file type', 'error');
      }
    }
  }, [showToast]);

  useEffect(() => {
    if (isProjectModalOpen) {
      fetchVehicleTemplates();
    }
  }, [isProjectModalOpen]);

  const fetchVehicleTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = getAccessToken();
      const response = await fetch('/api/templates', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const userTemplates = (data.vehicleTemplates || []).map((t: VehicleTemplate) => ({
          ...t,
          isDefault: false
        }));
        setVehicleTemplates([defaultSGMTemplate, ...userTemplates]);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const getNodeConfigList = (template: VehicleTemplate | null) => {
    if (!template) {
      return [
        { key: 'VPI', label: 'VPI', fullName: 'Vehicle Program Initiation', required: false },
        { key: 'SF', label: 'SF', fullName: 'Styling Freeze', required: false },
        { key: 'VDR', label: 'VDR', fullName: 'Verified Data Release', required: true },
        { key: 'VTC', label: 'VTC', fullName: 'Vehicle Testing Complete', required: false },
        { key: 'VDC', label: 'VDC', fullName: 'Validation Documentation Complete', required: false },
        { key: 'MC1', label: 'MC1', fullName: 'Matching 1', required: false },
        { key: 'MC2', label: 'MC2', fullName: 'Matching 2', required: false },
        { key: 'APPV', label: 'APPV', fullName: 'Advanced Process Validation Vehicle', required: true },
        { key: 'PPV', label: 'PPV', fullName: 'Process Validation Vehicle', required: false },
        { key: 'S', label: 'S', fullName: 'Salable', required: true },
        { key: 'SORP', label: 'SORP', fullName: 'Start Of Regular Production', required: true },
      ];
    }
    return template.milestones.map(m => ({
      key: m.name,
      label: m.name,
      fullName: m.description,
      required: m.required
    }));
  };

  const handleClose = () => {
    closeProjectModal();
    setTimeout(() => {
      setCurrentStep(1);
      setSelectedTemplate('');
      setSelectedTemplateData(null);
      setCreationMethod(null);
      setUploadedFile(null);
      setAiExtractedData(null);
      setName('');
      setDescription('');
      setSupplier('');
      setNodes({});
      setCreatedProjectId(null);
      setShowComponentTemplateModal(false);
      setPreviewTemplate(null);
    }, 300);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = vehicleTemplates.find(t => t.id === templateId);
    setSelectedTemplateData(template || null);
  };

  const handleTemplateConfirm = () => {
    if (!selectedTemplate) {
      showToast('Please select a template', 'warning');
      return;
    }
    setCurrentStep(2);
  };

  const handleMethodSelect = (method: 'import' | 'manual') => {
    setCreationMethod(method);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleAnalyzeDocument = async () => {
    if (!uploadedFile) return;
    
    setIsAnalyzing(true);
    try {
      const result = await extractProjectInfoFromDocument(uploadedFile);
      
      if (result.success && result.nodes) {
        setAiExtractedData({
          projectName: result.projectName,
          nodes: result.nodes,
        });
        
        if (result.projectName) setName(result.projectName);
        if (result.nodes) setNodes(result.nodes);
        
        showToast('Document analyzed successfully!', 'success');
        setCurrentStep(3);
      } else {
        showToast('AI extraction failed. Please use Manual Create.', 'error');
        setCreationMethod('manual');
      }
    } catch (error) {
      showToast('Analysis failed. Please use Manual Create.', 'error');
      setCreationMethod('manual');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleMethodConfirm = () => {
    if (!creationMethod) {
      showToast('Please select a creation method', 'warning');
      return;
    }
    
    if (creationMethod === 'import') {
      if (!uploadedFile) {
        showToast('Please upload a file first', 'warning');
        return;
      }
      handleAnalyzeDocument();
    } else {
      setCurrentStep(3);
    }
  };

  const handleNodeChange = (key: string, value: string) => {
    setNodes(prev => ({ ...prev, [key]: value }));
  };

  const handleCreateProject = async () => {
    if (!name.trim()) {
      showToast('Please enter project name', 'error');
      return;
    }

    const nodeConfigList = getNodeConfigList(selectedTemplateData);
    const requiredNodes = nodeConfigList.filter(n => n.required);
    const nodesRecord = nodes as Record<string, string | undefined>;
    for (const node of requiredNodes) {
      if (!nodesRecord[node.key]) {
        showToast(`Please fill in ${node.label} (${node.fullName})`, 'error');
        return;
      }
    }

    const newProject: Project = {
      id: `temp_${Date.now()}`,
      user_id: user?.id || '',
      name,
      description,
      supplier,
      status: 'active',
      nodes: nodes as VehicleNodes,
      taskList: [],
      version: '1.0',
      majorVersion: 1,
      minorVersion: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      vehicleMilestoneTemplate: selectedTemplateData || undefined,
    };

    setCurrentProject(newProject);
    
    setCurrentStep(4);
    showToast('Project initialized! Click "Generate Timing" or save manually.', 'success');
  };

  const handleGenerateTiming = async () => {
    const currentProject = useProjectStore.getState().currentProject;
    if (!currentProject?.nodes?.VDR || !currentProject?.nodes?.SORP) {
      showToast('Please set VDR and SORP node dates first', 'error');
      return;
    }
    setShowComponentTemplateModal(true);
  };

  const handleComponentTemplateSelect = (template: ComponentTemplate) => {
    setShowComponentTemplateModal(false);
    generateTimingWithTemplate(template);
  };

  const generateTimingWithTemplate = async (componentTemplate: ComponentTemplate) => {
    try {
      const currentProject = useProjectStore.getState().currentProject;
      if (!currentProject?.nodes?.VDR || !currentProject?.nodes?.SORP) {
        showToast('Please set VDR and SORP node dates first', 'error');
        return;
      }

      const functionalNodeMapping = extractFunctionalNodeMapping(selectedTemplateData as any);
      const customVehicleNodes = extractCustomVehicleNodes(selectedTemplateData as any);
      
      const processedTemplate = replaceFunctionalNodesInTemplate(
        componentTemplate.phases,
        functionalNodeMapping
      );

      const generatedTasks = calculateTaskDates(
        processedTemplate,
        currentProject.nodes,
        functionalNodeMapping,
        false,
        customVehicleNodes
      );

      const tasksWithId: Task[] = generatedTasks.map((task, index) => ({
        ...task,
        id: `task_${Date.now()}_${index}`,
        owner: '',
        status: 'not-started' as Task['status'],
      }));

      useProjectStore.getState().setTasks(tasksWithId);
      
      showToast('Timing generated successfully!', 'success');
      handleClose();
    } catch (error) {
      console.error('Generate timing error:', error);
      showToast('Failed to generate timing', 'error');
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Choose OEM Template</h3>
        <p className="text-sm text-gray-500 mt-1">Select a template to define vehicle nodes</p>
      </div>

      {loadingTemplates ? (
        <div className="flex justify-center py-8">
          <Loader size="md" />
        </div>
      ) : (
        <div className="grid gap-4 max-h-[50vh] overflow-y-auto pr-2">
          {vehicleTemplates.map((template) => (
            <div
              key={template.id}
              onClick={() => handleTemplateSelect(template.id)}
              className={`
                relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200
                ${selectedTemplate === template.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex items-start space-x-4">
                <AnimatedCheckbox 
                  checked={selectedTemplate === template.id} 
                  size="sm" 
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <h4 className="font-semibold text-gray-800">{template.oem}</h4>
                      {template.isDefault && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-blue-600 text-white rounded">默认模板</span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewTemplate(template);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="预览模板"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {template.milestones.slice(0, 6).map((node) => (
                      <span key={node.id} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                        {node.name}
                      </span>
                    ))}
                    {template.milestones.length > 6 && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                        +{template.milestones.length - 6} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-center">
        <button
          onClick={() => {
            handleClose();
            setTimeout(() => openTemplateShopModal(), 300);
          }}
          className="text-sm text-gray-400 italic hover:text-blue-600 transition-colors"
        >
          No template? Go to Template Shop to create
        </button>
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={handleTemplateConfirm}
          className="group flex items-center bg-transparent border-none cursor-pointer"
        >
          <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-gray-700 pr-4">
            Next Step
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
          </span>
          <svg
            className="w-5 h-4 text-gray-700 transform -translate-x-1 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Choose Creation Method</h3>
        <p className="text-sm text-gray-500 mt-1">Import from file or create manually</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div
          onClick={() => handleMethodSelect('import')}
          className={`
            relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 text-center
            ${creationMethod === 'import' 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
            }
          `}
        >
          <AnimatedCheckbox 
            checked={creationMethod === 'import'} 
            size="md" 
          />
          <FileSpreadsheet className="w-10 h-10 text-blue-600 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-800">Import from Files</h4>
          <p className="text-xs text-gray-500 mt-2">Upload Project Timing Document</p>
        </div>

        <div
          onClick={() => handleMethodSelect('manual')}
          className={`
            relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 text-center
            ${creationMethod === 'manual' 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
            }
          `}
        >
          <AnimatedCheckbox 
            checked={creationMethod === 'manual'} 
            size="md" 
          />
          <Edit3 className="w-10 h-10 text-green-600 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-800">Manual Create</h4>
          <p className="text-xs text-gray-500 mt-2">Fill in project information manually</p>
        </div>
      </div>

      {creationMethod === 'import' && (
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.txt,.pdf,.doc,.docx"
            onChange={handleFileUpload}
            className="hidden"
          />
          
          {!uploadedFile ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
              <p className="text-xs text-gray-400 mt-1">Support: Excel, CSV, PDF, Word, TXT</p>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-gray-800">{uploadedFile.name}</p>
                  <p className="text-xs text-gray-500">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <button
                onClick={() => setUploadedFile(null)}
                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500"
              >
                ×
              </button>
            </div>
          )}

          <p className="text-xs text-gray-400 italic text-center">
            Example: SGM SOR B&D File
          </p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button
          onClick={() => setCurrentStep(1)}
          className="group flex items-center bg-transparent border-none cursor-pointer"
        >
          <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-gray-700">
            Back
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
          </span>
        </button>
        <button
          onClick={handleMethodConfirm}
          className="group flex items-center bg-transparent border-none cursor-pointer"
        >
          <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-gray-700 pr-4">
            Next Step
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
          </span>
          <svg
            className="w-5 h-4 text-gray-700 transform -translate-x-1 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const nodeConfigList = getNodeConfigList(selectedTemplateData);
    
    return (
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Project Information</h3>
          <p className="text-sm text-gray-500 mt-1">Fill in basic info and vehicle node dates</p>
        </div>

        {aiExtractedData && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-3">
            <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">AI has pre-filled data from your document</p>
              <p className="text-xs text-blue-600 mt-1">Please review and modify if needed</p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter project name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter project description"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supplier
            </label>
            <input
              type="text"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="Enter supplier name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center mb-3">
            <h3 className="text-sm font-medium text-gray-700">Vehicle Node Dates</h3>
            <div className="ml-2 flex items-center text-xs text-gray-500">
              <AlertCircle className="w-3 h-3 mr-1" />
              <span>* Required</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {nodeConfigList.map((node) => (
              <div key={node.key} className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">
                  {node.label} ({node.fullName})
                  {node.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="date"
                  value={(nodes as Record<string, string | undefined>)[node.key] || ''}
                  onChange={(e) => handleNodeChange(node.key, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required={node.required}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setCurrentStep(2)}
            className="group flex items-center bg-transparent border-none cursor-pointer"
          >
            <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-gray-700">
              Back
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
            </span>
          </button>
          <button
            onClick={handleCreateProject}
            disabled={isCreating}
            className="group flex items-center bg-transparent border-none cursor-pointer disabled:opacity-50"
          >
            <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-gray-700 pr-4">
              Next Step
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
            </span>
            <svg
              className="w-5 h-4 text-gray-700 transform -translate-x-1 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="text-center py-8 space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <Check className="w-10 h-10 text-green-600" />
      </div>

      <div>
        <h3 className="text-xl font-bold text-gray-800">Project Created Successfully!</h3>
      </div>

      <div className="flex justify-center">
        <Button 
          onClick={handleGenerateTiming}
          className="w-72"
          leftIcon={<Sparkles className="w-4 h-4" />}
        >
          Generate Timing Now
        </Button>
      </div>

      <div className="flex justify-center space-x-3">
        <Button variant="ghost" onClick={handleClose} className="w-72">
          Skip for Now
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isProjectModalOpen}
      onClose={handleClose}
      title="New Project Assistant"
      size="lg"
      showCloseButton={currentStep !== 4}
    >
      <div className="space-y-6 relative">
        <Stepper steps={steps} currentStep={currentStep} />
        
        <div className="mt-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {isAnalyzing && (
          <div className="absolute -inset-6 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10">
            <Loader size="lg" />
          </div>
        )}
      </div>
      
      <ComponentTemplateSelectModal
        isOpen={showComponentTemplateModal}
        onClose={() => setShowComponentTemplateModal(false)}
        onSelect={handleComponentTemplateSelect}
      />
      
      {previewTemplate && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={() => setPreviewTemplate(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-bold text-gray-800">{previewTemplate.oem} - 整车里程碑模板预览</h3>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-60px)]">
              <p className="text-sm text-gray-500 mb-4">{previewTemplate.description}</p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-12">序号</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">节点名称</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">描述</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 w-16">必填</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 w-24">功能节点</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {previewTemplate.milestones.map((milestone) => (
                      <tr key={milestone.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-600">{milestone.sequence}</td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-800">{milestone.name}</td>
                        <td className="px-3 py-2 text-sm text-gray-600">{milestone.description}</td>
                        <td className="px-3 py-2 text-center">
                          {milestone.required ? (
                            <Check className="w-4 h-4 text-green-600 mx-auto" />
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {milestone.functionalNode !== 'N/A' ? (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                              {milestone.functionalNode}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
